import dayjs from "dayjs";
import { IdNameList, TaxDetail } from "../../../api/services/common/models";
import { SalesCreditDetail } from "../../../api/services/program/models";
import {
  ChildProgramsListResponse,
  ProjectStatus,
} from "../../../api/services/project/model";
import { BasicUserDetails } from "../../../api/services/reports/models";
import {
  BillingConfigurationDetailsFormKeyProps,
  BillingScheduleActivities,
  BillingTypeMilestoneConfigureTableData,
  BillingTypeScheduleConfigureTableData,
} from "../../../components/billingTypeConfigureTable/modelsAndSchemas/Models";
import { StatusBadgeType } from "../../../components/statusBadge/StatusBadge";
import {
  TaxConfigureTableData,
  TaxDetailsFormKeyProps,
} from "../../../components/taxDetails/modelsAndSchemas/Models";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  EMPTY_VALUE_DASH,
} from "../../../helpers/constants";
import { KeyValue } from "../../../helpers/types/common";
import { FacilitatorDetails } from "../components/createProjectTabs/tabs/project/components/facilitator/model";
import { projectAddFeesAndExpensesSiteCoreKeys } from "../siteCore/SiteCoreKeys";

export const AddProjectActivityFormKey = {
  activityType: "activityType",
  modality: "modality",
  topic: "topic",
  notes: "notes",
  onBehalfOfCoachId: "onBehalfOfCoachId",
  actualDurationInMinutes: "actualDurationInMinutes",
  defaultDuration: "defaultDuration",
  activityDate: "activityDate",
} as const;

export interface AddActivityFormProps {
  id?: string;
  activityType: string;
  modality: string;
  topic: string[];
  notes: string;
  onBehalfOfCoachId: string;
  actualDurationInMinutes: string;
  defaultDuration: string;
  activityDate: string;
}

export interface ActivityTimelineFilterFormType {
  filterBy: string;
  show: string;
  fromDate: string;
  toDate: string;
}

export interface ProjectActivityResponse {
  id: string;
  activityTypeId: string;
  activityName: string;
  modality?: string;
  topics?: IdNameList[];
  notes?: string;
  createdDate: string;
  createdBy: BasicUserDetails;
  programTemplateName?: string;
  transferredCountryName?: string;
  solutionOfferingsName?: string;
  isEditable: boolean;
  timelineType: number;
  primaryClosingReason?: string;
  secondaryClosingReason?: string;
  activityDate: string;
  defaultDurationInMinutes: number;
  actualDurationInMinutes: string;
  onBehalfOfFacilitator?: BasicUserDetails;
}

export const ShowFilterKeys = {
  AllKey: "ShowFilter.All",
  ThisMonthKey: "ShowFilter.ThisMonth",
  LastMonthKey: "ShowFilter.LastMonth",
  DurationKey: "ShowFilter.Duration",
};

export const ProjectActivityTimelineFilterFormKey = {
  filterBy: "filterBy",
  show: "show",
  fromDate: "fromDate",
  toDate: "toDate",
} as const;

export const ProjectStatusFormKey = {
  status: "status",
  date: "date",
  notes: "notes",
} as const;

export const AddActivityDefaultFormValue = {
  [AddProjectActivityFormKey.activityType]: "",
  [AddProjectActivityFormKey.modality]: "",
  [AddProjectActivityFormKey.topic]: [],
  [AddProjectActivityFormKey.notes]: "",
  [AddProjectActivityFormKey.onBehalfOfCoachId]: "",
  [AddProjectActivityFormKey.actualDurationInMinutes]: "",
  [AddProjectActivityFormKey.defaultDuration]: "",
  [AddProjectActivityFormKey.activityDate]: "",
};

export type ProjectFeeSectionKey = "feeDetails" | "taxDetails";

export const ProjectFeeTabFormKey = {
  id: "id",
  billingTypeId: "billingTypeId",
  currencyId: "currencyId",
  billingTriggerId: "billingTriggerId",
  feeWithFormat: "feeWithFormat",
  fee: "fee",
  earlyPayPercentage: "earlyPayPercentage",
  earlyPayDays: "earlyPayDays",
  minimumTransferFee: "minimumTransferFee",
  proBonoCategoryId: "proBonoCategoryId",
  minimumTransferFeeWithFormat: "minimumTransferFeeWithFormat",
  schedulePeriod: "schedulePeriod",
  feePaymentType: "feePaymentType",
  milestones: "milestones",
  schedules: "schedules",
  feePercentage: "feePercentage",
  activities: "activities",
  amountWithFormat: "scheduleAmountWithFormat",
  amount: "scheduleAmount",
  billingSchedules: "billingSchedules",
  scheduleFrequency: "scheduleFrequency",
  status: "status",
  description: "description",
  //Tax details
  taxSearch: "taxSearch",
  taxAmount: "amount",
  taxPercentage: "percentage",
  tax: "tax",
  taxAmountWithFormat: "amountWithFormat",
  taxEntries: "taxEntries",
  taxes: "taxes",
} as const;

export interface ProjectFeeTabFormType {
  id: string;
  earlyPayPercentage?: string;
  billingTypeId: string;
  billingTriggerId?: string;
  currencyId: string;
  fee: string;
  feeWithFormat: string;
  earlyPayDays?: string;
  proBonoCategoryId?: string;
  minimumTransferFee?: string;
  minimumTransferFeeWithFormat?: string;
  milestones?: string;
  schedulePeriod?: string;
  schedules?: string;
  feePaymentType?: string;
  activities?: BillingScheduleActivities[];
  feePercentage?: string;
  amountWithFormat?: string;
  amount?: string;
  scheduleFrequency?: string;
  billingSchedules?:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[];
  description?: string;
  status?: string;
  //Tax details
  tax?: string;
  taxPercentage?: string;
  taxSearch?: string;
  taxAmountWithFormat?: string;
  taxEntries?: TaxConfigureTableData[];
  taxAmount?: string;
}

export const createProjectProgramFeeTabDefaultValue = {
  [ProjectFeeTabFormKey.billingTypeId]:
    BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling,
  [ProjectFeeTabFormKey.id]: "",
  [ProjectFeeTabFormKey.billingTriggerId]: "",
  [ProjectFeeTabFormKey.currencyId]: "",
  [ProjectFeeTabFormKey.fee]: "",
  [ProjectFeeTabFormKey.earlyPayDays]: "",
  [ProjectFeeTabFormKey.feeWithFormat]: "",
  [ProjectFeeTabFormKey.proBonoCategoryId]: "",
  [ProjectFeeTabFormKey.earlyPayPercentage]: "",
  [ProjectFeeTabFormKey.minimumTransferFee]: "",
  [ProjectFeeTabFormKey.minimumTransferFeeWithFormat]: "",
  [ProjectFeeTabFormKey.feePaymentType]: "",
  [ProjectFeeTabFormKey.schedulePeriod]: "",
  [ProjectFeeTabFormKey.schedules]: "",
  [ProjectFeeTabFormKey.milestones]: "",
  [ProjectFeeTabFormKey.activities]: [],
  [ProjectFeeTabFormKey.feePercentage]: "",
  [ProjectFeeTabFormKey.amountWithFormat]: "",
  [ProjectFeeTabFormKey.amount]: "",
  [ProjectFeeTabFormKey.billingSchedules]: [],
  [ProjectFeeTabFormKey.description]: "",
  [ProjectFeeTabFormKey.scheduleFrequency]: "",
  [ProjectFeeTabFormKey.status]: "",
  //Tax details
  [ProjectFeeTabFormKey.taxSearch]: "",
  [ProjectFeeTabFormKey.taxPercentage]: "",
  [ProjectFeeTabFormKey.taxAmount]: "",
  [ProjectFeeTabFormKey.tax]: "",
  [ProjectFeeTabFormKey.taxAmountWithFormat]: "",
  [ProjectFeeTabFormKey.taxEntries]: [],
};

export const billingConfigurationDetailsFormKeys: BillingConfigurationDetailsFormKeyProps =
  {
    billingTypeIdKey: ProjectFeeTabFormKey.billingTypeId,
    schedulePeriodKey: ProjectFeeTabFormKey.schedulePeriod,
    feeKey: ProjectFeeTabFormKey.fee,
    scheduleKey: ProjectFeeTabFormKey.schedules,
    feePercentageKey: ProjectFeeTabFormKey.feePercentage,
    amountWithFormatKey: ProjectFeeTabFormKey.amountWithFormat,
    amountKey: ProjectFeeTabFormKey.amount,
    scheduleFrequencyKey: ProjectFeeTabFormKey.scheduleFrequency,
    milestoneKey: ProjectFeeTabFormKey.milestones,
    activitiesKey: ProjectFeeTabFormKey.activities,
    billingSchedulesKey: ProjectFeeTabFormKey.billingSchedules,
    feePaymentTypeKey: ProjectFeeTabFormKey.feePaymentType,
    descriptionKey: ProjectFeeTabFormKey.description,
    statusKey: ProjectFeeTabFormKey.status,
  };

export const taxDetailsFormKeys: TaxDetailsFormKeyProps = {
  taxSearchKey: ProjectFeeTabFormKey.taxSearch,
  programFeeKey: ProjectFeeTabFormKey.fee,
  taxKey: ProjectFeeTabFormKey.tax,
  taxPercentageKey: ProjectFeeTabFormKey.taxPercentage,
  taxAmountWithFormatKey: ProjectFeeTabFormKey.taxAmountWithFormat,
  taxAmountKey: ProjectFeeTabFormKey.taxAmount,
  taxEntriesKey: ProjectFeeTabFormKey.taxEntries,
};

export const ProjectSalesCreditDetailsFormKeys = {
  clientRelationshipOwner: "clientRelationshipOwner",
  percentage: "percentage",
  officeName: "officeName",
} as const;

export interface SalesCreditTableRow {
  id?: string;
  localClientRelationshipOwnerId?: string;
  clientRelationshipOwner: string;
  officeName: string;
  firstName: string;
  lastName: string;
  email: string;
  percentage: string;
  [key: string]: any;
}

export const projectStatusResultKey = {
  comment: "comment",
  status: "status",
  createdByName: "createdByName",
  createdTimeStamp: "createdTimeStamp",
} as const;

export interface ProjectStatusListRow extends ProjectStatus {
  statusLabel: string;
}

export type ProjectTabSectionKey = "officeAndContactDetails";

type ProjectTabFormKeyOf = keyof ProjectTabFormType;

export const ProjectTabFormKey: { [K in ProjectTabFormKeyOf]: K } = {
  billingOffice: "billingOffice",
  salesOfficeId: "salesOfficeId",
  clientRelationShipOwner: "clientRelationShipOwner",
  billingContact: "billingContact",
  hrContact: "hrContact",
  projectManager: "projectManager",
  projectCoordinator: "projectCoordinator",
  salesCreditDetails: "salesCreditDetails",
  projectFacilitator: "projectFacilitator",
  facilitatorAssignDate: "facilitatorAssignDate",
  facilitatorsId: "facilitatorsId",
};

export const createProjectTabDefaultValue = {
  [ProjectTabFormKey.billingOffice]: "",
  [ProjectTabFormKey.salesOfficeId]: "",
  [ProjectTabFormKey.clientRelationShipOwner]: "",
  [ProjectTabFormKey.billingContact]: "",
  [ProjectTabFormKey.hrContact]: "",
  [ProjectTabFormKey.projectManager]: [],
  [ProjectTabFormKey.projectCoordinator]: [],
  [ProjectTabFormKey.salesCreditDetails]: [],
  [ProjectTabFormKey.projectFacilitator]: [],
  [ProjectTabFormKey.facilitatorAssignDate]: "",
  [ProjectTabFormKey.facilitatorsId]: "",
};

export interface ProjectKeyDetailsForm {
  projectName: string;
  client: string;
  billingOffice: string;
  clientBusinessUnit: string;
  clientCostCenter: string;
  poNumbers: string[];
  salesOfficeId: string;
  productLines: string[];
  primaryProductLine: string;
  projectFramework: string;
  primaryDeliveryCountry: string;
  primaryDeliveryOffice: string;
  dossierNumber?: string;
  actualClientId?: string;
}

export const CreateProjectFormKey = {
  client: "client",
  clientName: "clientName",
  projectName: "projectName",
  productLines: "productLines",
  primaryProductLine: "primaryProductLine",
  poNumbers: "poNumbers",
  clientBusinessUnit: "clientBusinessUnit",
  clientCostCenter: "clientCostCenter",
  projectFramework: "projectFramework",
  salesOfficeId: "salesOfficeId",
  billingOffice: "billingOffice",
  primaryDeliveryCountry: "primaryDeliveryCountry",
  primaryDeliveryOffice: "primaryDeliveryOffice",
  dossierNumber: "dossierNumber",
  actualClientId: "actualClientId",
} as const;

export interface CreateProjectFormTypeValues {
  client: string;
  clientName: string;
  projectName: string;
  productLines: string[];
  primaryProductLine: string;
  poNumbers: string[];
  clientBusinessUnit: string;
  clientCostCenter: string;
  projectFramework: string;
  salesOfficeId: string;
  billingOffice: string;
  primaryDeliveryCountry: string;
  primaryDeliveryOffice: string;
  dossierNumber: string;
  actualClientId: string;
}

export const projectKeyDetailsDefaultValue = {
  [CreateProjectFormKey.client]: "",
  [CreateProjectFormKey.billingOffice]: "",
  [CreateProjectFormKey.clientBusinessUnit]: "",
  [CreateProjectFormKey.clientCostCenter]: "",
  [CreateProjectFormKey.poNumbers]: [],
  [CreateProjectFormKey.salesOfficeId]: "",
  [CreateProjectFormKey.productLines]: [],
  [CreateProjectFormKey.projectFramework]: "",
  [CreateProjectFormKey.primaryDeliveryCountry]: EMPTY_VALUE_DASH,
  [CreateProjectFormKey.primaryDeliveryOffice]: "",
};

export interface ProjectTabFormType {
  billingOffice: string;
  salesOfficeId: string;
  clientRelationShipOwner: string;
  billingContact: string;
  hrContact: string;
  projectManager: string[];
  projectCoordinator: string[];
  salesCreditDetails?: SalesCreditDetail[];
  projectFacilitator?: FacilitatorDetails[];
  facilitatorAssignDate?: string;
  facilitatorsId?: string;
}

export const createProjectStatus: ProjectStatus = {
  id: "",
  status: "Status.Open",
  comment: "",
  createdByName: "",
  createdTimeStamp: dayjs().toString(),
};

export const ProjectFeesAndExpensesColumnKey = {
  projectIsChecked: "isChecked",
  projectFeesOrExpenseName: "name",
  projectTransactionDate: "transactionDate",
  projectType: "typeName",
  projectCategory: "expenseCategoryName",
  projectFeesOrExpenseType: "feeOrExpenseType",
  projectStatusId: "statusId",
  projectUnitRate: "unitRate",
  projectQuantity: "quantity",
  projectFeesOrExpenseAmount: "amount",
  projectBillingAmount: "billingAmount",
  projectTaxAmount: "tax",
  projectTotalInvoiceAmount: "totalAmount",
  projectComment: "comments",
  projectStatus: "status",
} as const;

export const ADD_PROJECT_FEE_AND_EXPENSE_TYPES = {
  PROJECT_OR_PROJECT_FEE:
    projectAddFeesAndExpensesSiteCoreKeys.programProjectFeeRadioButton,
  PROJECT_EXPENSE: projectAddFeesAndExpensesSiteCoreKeys.expenseRadioButton,
  PROJECT_FEE: projectAddFeesAndExpensesSiteCoreKeys.projectFeeType,
};

export interface ProjectFeesAndExpenseRow {
  id: string;
  transactionDate: string;
  typeName: string;
  typeId?: string;
  name: string;
  expenseCategoryName: string;
  currencyName: string;
  feeOrExpenseType: string;
  status: string;
  description: string;
  statusId: string;
  unitRate: number;
  billingAmount: number;
  quantity: number;
  amount: number;
  taxDetails: TaxDetail[];
  tax: number;
  totalAmount: number;
  comments: string;
  isChecked: boolean;
}

export interface ProjectRaiseInvoiceFormType {
  feeOrExpenseName: string;
  type: string;
  category: string;
  feeOrExpenseType: string;
  billingAmount: string;
  taxAmount: string;
  totalInvoiceAmount: string;
  lineNumber: string;
}

export const ProjectRaiseInvoiceFormKey = {
  projectFeeOrExpenseName: "feeOrExpenseName",
  projectType: "type",
  projectCategory: "category",
  projectFeeOrExpenseType: "feeOrExpenseType",
  projectBillingAmount: "billingAmount",
  projectTaxAmount: "taxAmount",
  projectTotalInvoiceAmount: "totalInvoiceAmount",
  projectLineNumber: "lineNumber",
};

export const ProjectInvoiceReversalFormKey = {
  projectReversalReason: "reversalReason",
  projectReversalDate: "reversalDate",
  projectComments: "comments",
};

export type ProjectInvoice = KeyValue;

export interface ProjectFeesAndExpenseRow {
  id: string;
  transactionDate: string;
  typeName: string;
  typeId?: string;
  name: string;
  expenseCategoryName: string;
  currencyName: string;
  feeOrExpenseType: string;
  status: string;
  description: string;
  statusId: string;
  unitRate: number;
  billingAmount: number;
  quantity: number;
  amount: number;
  taxDetails: TaxDetail[];
  tax: number;
  totalAmount: number;
  comments: string;
  isChecked: boolean;
}

export interface ProjectInvoiceSummary {
  totalAmount: number;
  invoices: ProjectInvoiceItem[];
  currency: IdNameList;
}

export interface ProjectIssuedByUser {
  firstName: string;
  lastName: string;
  email: string;
}

export interface ProjectInvoicePurchaseOrder {
  id: string;
  number: string;
}

export interface ProjectInvoiceItem {
  id: string;
  number: string;
  date: string;
  statusId: string;
  totalInvoiceAmount: number;
  issuedBy: ProjectIssuedByUser;
  purchaseOrder: ProjectInvoicePurchaseOrder;
  isInvoiceReversed: boolean;
  reversalReason: string;
  reversalComments?: string;
  accountingPeriod: IdNameList;
}

export interface ProjectPurchaseOrderSummary {
  id: string;
  number: string;
}

export interface ProjectExpenseCategory {
  id: string;
  name: string;
}

export interface ProjectExpenseType {
  id: string;
  name: string;
}

export type ViewProjectInvoiceFeesAndExpensesListItem = {
  name: string;
  transactionDate: string;
  typeId: string;
  category: string;
  feesOrExpenseType: string | null;
  billingAmount: number;
  taxAmount: number;
  totalBillingAmount: number;
  expenseCategories: ProjectExpenseCategory;
  expenseTypes: ProjectExpenseType;
  expenseCategoryName?: string;
  expenseTypeName?: string;
  lineNumber: string;
};
export interface ViewProjectInvoiceResponse {
  id: string;
  projectId: string;
  number: string;
  date: string;
  statusId: StatusBadgeType;
  feeOrExpenseAmount: number;
  totalTaxAmount: number;
  totalInvoiceAmount: number;
  purchaseOrders: ProjectPurchaseOrderSummary;
  feesOrExpenses: ViewProjectInvoiceFeesAndExpensesListItem[];
  feeCurrency: IdNameList;
  paymentTerm: ProjectStatus;
  reversalReason?: string;
  reversalComments?: string;
  invoiceLanguage?: ProjectInvoicingLanguage;
  localInvoiceNumber: string;
}

export interface ProjectInvoicingLanguage {
  code: string;
  id: string;
  name: string;
}

export interface ProjectPurchaseOrder {
  poNumber: string;
  purchaseOrderId: string;
  statusId: string;
}

export const ProjectRaiseInvoicePODetailsFormKey = {
  projectPONumber: "poNumber",
  projectCurrentPOBalance: "currentPOBalance",
  projectPOShortage: "poShortage",
  projectInvoiceDate: "invoiceDate",
  projectInvoiceLanguage: "invoiceLanguage",
  projectPaymentTerms: "paymentTerms",
  localInvoiceNumber: "localInvoiceNumber",
};

export const ViewProjectInvoiceFeesAndExpenses = {
  projectName: "name",
  projectTransactionDate: "transactionDate",
  projectTypeId: "typeId",
  projectBillingAmount: "billingAmount",
  projectTaxAmount: "taxAmount",
  projectTotalBillingAmount: "totalBillingAmount",
  projectExpenseCategoryName: "expenseCategoryName",
  projectExpenseTypeName: "expenseTypeName",
  projectLineNumber: "lineNumber",
} as const;

export const ProjectAddOrEditExpenseOrFeeFormKey = {
  projectType: "type",

  projectExpenseIncurredById: "expenseIncurredById",
  projectExpenseTypeId: "expenseTypeId",
  projectExpenseCategoryId: "expenseCategoryId",
  projectExpenseUnitPrice: "expenseUnitPrice",
  projectExpenseQuantity: "expenseQuantity",

  projectTransactionDate: "transactionDate",
  projectBillingCurrency: "billingCurrency",
  projectBillingCurrencyName: "billingCurrencyName",
  projectBillingCurrencyExchangeRate: "billingCurrencyExchangeRate",
  projectBillingAmount: "billingAmount",
  projectBillingAmountWithFormat: "billingAmountWithFormat",
  projectComments: "comments",
  projectFeeOrExpenseAmountWithFormat: "feeOrExpenseAmountWithFormat",
  projectFeeOrExpenseCurrencyName: "feeOrExpenseCurrencyName",
  projectFeeOrExpenseName: "feeOrExpenseName",
  projectFeeOrExpenseCurrencyId: "feeOrExpenseCurrencyId",
  projectFeeOrExpenseAmount: "feeOrExpenseAmount",
  projectFeeOrExpenseFormattedAmount: "feeOrExpenseAmountFormatted",
  projectFeeTypeId: "feeTypeId",
  projectTaxSearch: "taxSearch",
  projectTax: "tax",
  projectTaxPercentage: "percentage",
  projectTaxAmount: "amount",
  projectTaxAmountWithFormat: "amountWithFormat",
  projectTaxEntries: "taxEntries",
  projectIsRateConverted: "isRateConverted",
};

export const projectAddExpenseFormInitialValue = {
  [ProjectAddOrEditExpenseOrFeeFormKey.projectType]:
    ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_OR_PROJECT_FEE,
  [ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseIncurredById]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseTypeId]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseCategoryId]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseUnitPrice]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseQuantity]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectTransactionDate]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrency]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyName]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmount]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmountWithFormat]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectComments]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmountWithFormat]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyName]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseName]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyId]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmount]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseFormattedAmount]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectFeeTypeId]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectTaxSearch]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectTax]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectTaxPercentage]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectTaxAmount]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectTaxAmountWithFormat]: "",
  [ProjectAddOrEditExpenseOrFeeFormKey.projectTaxEntries]: null,
};

export const ProjectInvoiceHistoryColumnKey = {
  projectNumber: "number",
  projectDate: "date",
  invoiceStatusId: "statusId",
  projectReversalReason: "reversalReason",
  projectTotalInvoiceAmount: "totalInvoiceAmount",
  projectIssuedBy: "issuedBy",
  projectPurchaseOrder: "purchaseOrder",
} as const;

export const ProjectAddExpenseOrFeeSummeryColumns = {
  projectTotalAmount: "totalAmount",
};

export const ChildProgramsListFormKey = {
  programName: "programName",
  keyNumber: "keyNumber",
  programStatus: "programStatus",
  clientManagedStatus: "clientManagedStatus",
  participantEmail: "participantEmail",
  participantMobileNumber: "participantMobileNumber",
  projectManager: "projectManager",
  country: "country",
  countryName: "countryName",
  createdDate: "createdDate",
  billingContactName: "billingContactName",
  hrContactName: "hrContactName",
  lineOfBusiness: "lineOfBusiness",
  clientRelationShipOwner: "clientRelationShipOwner",
  solutionCategory: "solutionCategory",
  programStartDate: "programStartDate",
};

export interface ChildProgramsListRowValue extends ChildProgramsListResponse {
  [key: string]: any;
}

export type ChildProgramsListDropdownFilterKey =
  | typeof ChildProgramsListFormKey.programStatus
  | typeof ChildProgramsListFormKey.solutionCategory
  | typeof ChildProgramsListFormKey.lineOfBusiness;

export const AddExistingProgramsSearchFormKey = {
  resetForm: "resetForm",
  client: "client",
  projectManagerId: "projectManagerId",
  programParticipantName: "programParticipantName",
  programParticipantNameFilter: "programParticipantNameFilter",
  keyNumber: "keyNumber",
  keyNumberFilter: "keyNumberFilter",
  clientManagedStatus: "clientManagedStatus",
  programStatus: "programStatus",
};

export const AddExistingProgramsResultFormKey = {
  isChecked: "isChecked",
  programName: "programName",
  keyNumber: "keyNumber",
  programStatus: "programStatus",
  clientManagedStatus: "clientManagedStatus",
  participantEmail: "participantEmail",
  participantMobileNumber: "participantMobileNumber",
  projectManager: "projectManager",
  createdDate: "createdDate",
};

export interface AddExistingProgramsResultRow
  extends ChildProgramsListRowValue {
  isProgramInvoiced?: boolean;
  isChecked?: boolean;
}
